import React, { useState, useEffect, useRef } from 'react';
import { UserProvider, useUser } from './contexts/UserContext';
import { TRACKS, PLACEHOLDER_AUDIO } from './constants';
import { SleepTracker } from './components/SleepTracker';
import { LegalModal } from './components/LegalModal';
import { AdBanner } from './components/AdBanner';
import type { Track, SleepLog, User } from './types';

// --- UTILS ---

const getPlaceholderImage = (title: string) => {
    // Generate a placeholder using placehold.co
    const encodedTitle = encodeURIComponent(title);
    return `https://placehold.co/800x800/222244/FFFFFF.png?text=${encodedTitle}`;
};

// --- SHARED COMPONENTS ---

// Define an interface for TrackCard's props for better type clarity
interface ITrackCardProps {
    track: Track;
    onClick: () => void;
    isHistoryView?: boolean;
}

const TrackCard: React.FC<ITrackCardProps> = ({ 
    track,
    onClick,
    isHistoryView = false
}) => {
    const [imageError, setImageError] = useState(false);
    // Removed unused 'category' param from getPlaceholderImage call
    const imageUrl = !imageError && track.image ? track.image : getPlaceholderImage(track.title);

    return (
        <div className="card"
             style={{background: `radial-gradient(circle at top left, ${track.coverColor}, #141428)`}}
             onClick={onClick}
        >
            <div className="artwork" style={{background: `linear-gradient(145deg, ${track.coverColor}, #15163c)`}}>
                <img 
                    src={imageUrl} 
                    alt={track.title} 
                    onError={() => setImageError(true)}
                    style={{width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0}} 
                />
                <div style={{position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)'}}></div>
                
                <div className="artwork-label" style={{zIndex: 2}}>
                    <span>{track.category}</span>
                    {isHistoryView ?
                        <span className="track-duration">▶ Play</span> :
                        <span className="track-duration">{track.duration ? `${track.duration} min` : 'Loop'}</span>
                    }
                </div>
            </div>
            <div style={{marginTop: '8px'}}>
                <div className="track-title">{track.title}</div>
                <div className="track-meta">
                    <span>{track.type.charAt(0).toUpperCase() + track.type.slice(1)}</span>
                    {track.isNew && <span className="badge badge-new">NEW</span>}
                </div>
            </div>
        </div>
    );
};

const CheckInModal = ({ onClose, onSave }: { onClose: () => void; onSave: (log: Partial<SleepLog>) => void }) => {
    const [bedtime, setBedtime] = useState('');
    const [wakeTime, setWakeTime] = useState('');
    const [quality, setQuality] = useState<number>(3);
    const [notes, setNotes] = useState('');

    const calculateDuration = (start: string, end: string) => {
        if (!start || !end) return 0;
        const [startH, startM] = start.split(':').map(Number);
        const [endH, endM] = end.split(':').map(Number);
        
        let duration = (endH + endM / 60) - (startH + startM / 60);
        if (duration < 0) duration += 24;
        
        return parseFloat(duration.toFixed(1));
    };

    const handleSubmit = () => {
        const duration = calculateDuration(bedtime, wakeTime);
        onSave({ 
            bedtime, 
            wakeTime, 
            quality, 
            notes, 
            date: new Date().toISOString().split('T')[0],
            duration
        });
    };

    return (
        <div className="paywall-backdrop">
             <div className="paywall-card" style={{background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)'}}>
                <div className="paywall-header">
                     <h2>Morning Check-in ☀️</h2>
                     <p>How did you sleep last night?</p>
                </div>
                <div className="space-y-4 my-4">
                     <div>
                        <label className="text-xs text-slate-400">Bedtime / Wake Up</label>
                        <div className="flex gap-2 mt-1">
                            <input type="time" className="bg-slate-900 border border-slate-700 rounded p-2 text-white w-full" value={bedtime} onChange={e => setBedtime(e.target.value)} />
                            <input type="time" className="bg-slate-900 border border-slate-700 rounded p-2 text-white w-full" value={wakeTime} onChange={e => setWakeTime(e.target.value)} />
                        </div>
                     </div>
                     <div>
                        <label className="text-xs text-slate-400 text-center block mb-2">Sleep Quality</label>
                        <div className="flex justify-between text-2xl">
                             {[1,2,3,4,5].map(q => (
                                 <button key={q} onClick={() => setQuality(q)} className={`transition-transform ${quality === q ? 'scale-125' : 'opacity-50'}`}>
                                     {['😫','😔','😐','🙂','😊'][q-1]}
                                 </button>
                             ))}
                        </div>
                     </div>
                     <textarea 
                        className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white text-sm" 
                        placeholder="Any notes? (Dreams, interruptions...)"
                        rows={2}
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                     />
                </div>
                <button onClick={handleSubmit} className="btn btn-primary w-full">Save Check-in</button>
                <button onClick={onClose} className="btn btn-ghost w-full mt-2 text-xs">Skip for today</button>
             </div>
        </div>
    );
};

// --- VIEW COMPONENTS (Defined OUTSIDE main app to prevent re-renders) ---

interface HomeViewProps {
    user: User;
    activeView: string;
    setShowCheckIn: (val: boolean) => void;
    playTrack: (id: string) => void;
    handleTrackClick: (track: Track) => void;
    recommended: Track[];
}

const HomeView: React.FC<HomeViewProps> = ({ 
    user, activeView, setShowCheckIn, playTrack, handleTrackClick, recommended 
}) => {
    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

    return (
        <section className={`view ${activeView === 'home' ? 'active' : ''}`}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                <div>
                    <div className="chip chip-strong">Slumber Archive</div>
                    <h2 style={{marginTop:'8px', fontSize:'20px'}}>{greeting}, {user.name || 'Dreamer'}.</h2>
                </div>
            </div>

            {/* Morning Prompt Card */}
            {!user.sleepLog.some(l => l.date === new Date().toISOString().split('T')[0]) && hour < 12 && (
                <div className="card mb-4 flex items-center justify-between" onClick={() => setShowCheckIn(true)} style={{background: 'linear-gradient(90deg, #1e293b, #0f172a)'}}>
                    <div>
                        <div className="text-sm font-bold text-yellow-300">Morning Check-in</div>
                        <div className="text-xs text-slate-400">Log your sleep for last night</div>
                    </div>
                    <button className="btn btn-small btn-secondary">Log</button>
                </div>
            )}

            <div className="card card-hero">
                <div>
                    <small>Start here</small>
                    <h2>Tonight's Wind-Down</h2>
                    <p>We've picked a soft mix of sounds and a short story to ease you into sleep.</p>
                </div>
                <div className="card-hero-footer">
                    <span className="tag">Recommended blend</span>
                    <button onClick={() => playTrack(recommended[0]?.id || TRACKS[0].id)} className="btn btn-secondary btn-small">Play mix</button>
                </div>
            </div>

            <div className="section-header"><h3>Recommended for you</h3><small>Based on your preferences</small></div>
            <div className="scroll-row">{recommended.map(t => <div key={t.id} className="min-w-[150px]"><TrackCard track={t} onClick={() => handleTrackClick(t)} /></div>)}</div>

            <div className="section-header"><h3>Soundscapes</h3></div>
            <div className="scroll-row">{TRACKS.filter(t => t.type === 'soundscapes').map(t => <div key={t.id} className="min-w-[150px]"><TrackCard track={t} onClick={() => handleTrackClick(t)} /></div>)}</div>

            <div className="section-header"><h3>Stories</h3></div>
            <div className="scroll-row">{TRACKS.filter(t => t.type === 'stories').map(t => <div key={t.id} className="min-w-[150px]"><TrackCard track={t} onClick={() => handleTrackClick(t)} /></div>)}</div>
            
            <AdBanner />
        </section>
    );
};

interface LibraryViewProps {
    user: User;
    activeView: string;
    updateUser: (u: Partial<User>) => void;
    playTrack: (id: string) => void;
    handleTrackClick: (track: Track) => void;
}

const LibraryView: React.FC<LibraryViewProps> = ({ user, activeView, updateUser, playTrack, handleTrackClick }) => {
    const [filter, setFilter] = useState('favourites');
    const [search, setSearch] = useState('');
    const [subtab, setSubtab] = useState('all');

    let filtered: Track[] = TRACKS;
    
    if (filter === 'history') {
            filtered = user.history
            .map(id => TRACKS.find(t => t.id === id))
            .filter((t): t is Track => !!t)
            .slice(0, 10);
    }
    else if (filter === 'favourites') filtered = TRACKS.filter(t => user.favourites.includes(t.id));
    else if (filter === 'all') filtered = TRACKS; // Replaced 'free' with 'all' or similar generic category
    else filtered = TRACKS.filter(t => t.type === filter || t.category.toLowerCase().includes(filter));

    if (filter === 'stories' && subtab !== 'all') {
        filtered = filtered.filter(t => t.subcategory === subtab);
    }

    if (search) filtered = filtered.filter(t => t.title.toLowerCase().includes(search.toLowerCase()));

    return (
        <section className={`view ${activeView === 'library' ? 'active' : ''}`}>
            <h2 style={{fontSize:'20px', marginBottom:'2px'}}>Library</h2>
            <input className="search-input" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
            
            <div className="category-tabs">
                {['history', 'favourites', 'all', 'soundscapes', 'noise', 'music', 'stories'].map(f => (
                    <button key={f} className={`btn-tag ${filter === f ? 'active' : ''}`} onClick={() => { setFilter(f); setSubtab('all'); }}>
                        {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                ))}
            </div>

            {filter === 'stories' && (
                    <div className="category-tabs mt-2">
                    {['all', 'boring-history', 'cosy', 'fantasy', 'bedtime-classics', 'sleep-journeys'].map(st => (
                        <button key={st} className={`btn-tag ${subtab === st ? 'active' : ''}`} onClick={() => setSubtab(st)}>
                            {st.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')}
                        </button>
                    ))}
                </div>
            )}
            
            {filter === 'history' && user.history.length > 0 && (
                <div className="flex justify-end my-2">
                    <button 
                        onClick={() => {
                            if (window.confirm('Are you sure you want to clear your listening history?')) {
                                updateUser({ history: [] });
                            }
                        }} 
                        className="text-xs text-red-400 hover:text-red-300 transition-colors cursor-pointer"
                    >
                        Clear History
                    </button>
                </div>
            )}

            <div className="track-grid">
                {filtered.length > 0 ? filtered.map(t => (
                    <TrackCard 
                        key={t.id} 
                        track={t} 
                        isHistoryView={filter === 'history'}
                        onClick={() => filter === 'history' ? playTrack(t.id) : handleTrackClick(t)}
                    />
                )) : <div className="empty-state" style={{gridColumn:'1/-1'}}>{filter === 'history' ? "No history yet. Play some tracks!" : "No tracks found."}</div>}
            </div>
        </section>
    );
};

const ProfileView = ({ 
    user, activeView, setActiveView, 
    updateUser, setShowCheckIn, setShowLegal, resetApp 
}: any) => (
    <section className={`view ${activeView === 'profile' ? 'active' : ''}`}>
            <h2 style={{fontSize:'20px', marginBottom:'12px'}}>Profile</h2>
            <div className="profile-card">
                <div className="profile-card-header">
                    <div className="avatar">🌙</div>
                    <div>
                        <div style={{fontSize:'14px'}}>{user.name || 'Dreamer'}</div>
                        <div className="profile-meta">Offline Mode</div>
                    </div>
                </div>
            </div>
            
            <div className="toggle-row">
                <div><div style={{fontSize:'14px'}}>Bedtime reminder</div></div>
                <button className={`toggle ${user.bedtimeReminderOn ? 'on' : ''}`} onClick={() => updateUser({bedtimeReminderOn: !user.bedtimeReminderOn})}><div className="toggle-thumb"></div></button>
            </div>
            <div className="divider"></div>
            
            <div className="link-row" onClick={() => setActiveView('sleep-log')}>
            <div><span className="label">Sleep log</span><br/><span className="sub">Morning check-ins</span></div>
            <div className="flex items-center gap-2">
                    <span className="text-link" onClick={(e) => { e.stopPropagation(); setShowCheckIn(true); }}>+ Add</span>
                    <span>➜</span>
            </div>
            </div>
            <div className="sleep-log-preview">
                {user.sleepLog.length ? user.sleepLog.slice(0, 3).map((l: any, i: number) => <div key={i} className="sleep-log-entry"><span>{l.date}</span><span>{['😫','😔','😐','🙂','😊'][l.quality-1]}</span></div>) : <div className="empty-state">No logs yet.</div>}
            </div>

            <div className="divider"></div>
            
            <div className="link-row" onClick={() => setShowLegal(true)}>
            <div><span className="label">Legal & Compliance</span><br/><span className="sub">Terms & Privacy</span></div>
            <div>ℹ</div>
            </div>

            <div className="mt-8 pt-4 border-t border-slate-800 text-center">
                <button 
                onClick={() => {
                    if(confirm("This will reset all local app data. Are you sure?")) {
                        resetApp();
                    }
                }}
                className="text-xs text-red-500/60 hover:text-red-500 transition-colors"
                >
                Reset App Data
                </button>
            </div>
            
            <AdBanner />
    </section>
);

const DetailView = ({ selectedTrack, activeView, setActiveView, playTrack, toggleFavourite, user }: any) => {
    const [imageError, setImageError] = useState(false);
    if (!selectedTrack) return null;
    
    // Removed unused 'category' param
    const imageUrl = !imageError && selectedTrack.image ? selectedTrack.image : getPlaceholderImage(selectedTrack.title);

    return (
        <section className={`view ${activeView === 'detail' ? 'active' : ''}`} id="track-detail-view">
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                <button onClick={() => setActiveView('library')} className="btn btn-ghost">← Back</button>
                <div className="pill">Track Detail</div>
            </div>
            <div className="track-detail-art" style={{background: `radial-gradient(circle at top, ${selectedTrack.coverColor}, #020617)`}}>
                <img 
                    src={imageUrl} 
                    alt={selectedTrack.title} 
                    onError={() => setImageError(true)}
                    style={{width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0}} 
                />
                <div style={{position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(2,6,23,0.9) 10%, transparent 60%)'}}></div>
                
                <div className="track-detail-header">
                    <span className="pill-soft" style={{position:'relative', zIndex: 10}}>{selectedTrack.category}</span>
                    <div></div>
                </div>
                <div className="track-detail-main">
                    <h2>{selectedTrack.title}</h2>
                    <div className="track-detail-meta"><span>{selectedTrack.duration} min</span><span>•</span><span>{selectedTrack.type}</span></div>
                    <div className="track-detail-controls">
                        <button onClick={() => playTrack(selectedTrack.id)} className="btn btn-primary btn-small">▶ PLAY</button>
                        <button onClick={() => toggleFavourite(selectedTrack.id)} className="btn-icon btn-icon-circle" style={{background: 'rgba(255,255,255,0.1)'}}>{user.favourites.includes(selectedTrack.id) ? '♥' : '♡'}</button>
                    </div>
                </div>
            </div>
            <div style={{marginTop: '20px'}}>
                <h3>About this track</h3>
                <p className="text-sm mt-2">{selectedTrack.description}</p>
            </div>
        </section>
    );
};

const PlayerView = ({ activeView, setActiveView, playerState, user, handleSeek, skipTime, togglePlay, toggleLoop, timerDuration, handleSetTimer, toggleFavourite }: any) => {
    const [imageError, setImageError] = useState(false);
    const track = TRACKS.find(t => t.id === playerState.trackId);
    
    // Reset error state when track changes
    useEffect(() => {
        setImageError(false);
    }, [playerState.trackId]);

    if (!track) return null;
    
    // Removed unused 'category' param
    const imageUrl = !imageError && track.image ? track.image : getPlaceholderImage(track.title);

    const formatTime = (time: number) => {
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60);
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    return (
        <section className={`view ${activeView === 'player' ? 'active' : ''}`} id="player-view">
                <div className="player-top-bar">
                <button onClick={() => setActiveView('home')} className="btn btn-ghost">↓ Close</button>
                <div>Now Playing</div>
                <button className="btn-icon relative">
                    ⏱ {timerDuration && <span className="absolute top-1 right-1 w-2 h-2 bg-indigo-500 rounded-full"></span>}
                </button>
                </div>
                <div className="player-art" style={{background: `radial-gradient(circle at top, ${track.coverColor}, #111827)`}}>
                    <img 
                        src={imageUrl} 
                        alt={track.title} 
                        onError={() => setImageError(true)}
                        style={{width: '100%', height: '100%', objectFit: 'cover'}} 
                    />
                </div>
                <div className="player-info">
                    <h2>{track.title}</h2>
                    <p>{track.description}</p>
                </div>
                
                {/* Progress Bar */}
                <div className="progress-wrapper">
                    <input 
                    type="range" 
                    min="0" 
                    max="100" 
                    value={playerState.progress || 0} 
                    onChange={handleSeek}
                    className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #7b6cff ${playerState.progress}%, #334155 ${playerState.progress}%)`}}
                    />
                <div className="progress-times">
                    <span>{formatTime(playerState.currentTime)}</span>
                    <span>{track.duration > 0 ? `${track.duration}:00` : 'Loop'}</span>
                </div>
                </div>

                <div className="player-controls-main">
                <button onClick={() => skipTime(-30)} className="btn-icon btn-icon-circle">↺30</button>
                <button onClick={togglePlay} className="btn-icon btn-icon-lg">{playerState.isPlaying ? '⏸' : '▶'}</button>
                <button onClick={() => skipTime(30)} className="btn-icon btn-icon-circle">30↻</button>
                </div>
                
                <div className="player-controls-secondary">
                <div className="flex items-center">
                    <button onClick={() => track && toggleFavourite(track.id)} className="btn-icon btn-icon-circle">
                        {track && user.favourites.includes(track.id) ? '♥' : '♡'}
                    </button>
                </div>
                <div style={{textAlign:'right'}}>
                    <div className="flex items-center justify-end gap-3 mb-2">
                        {track.type !== 'stories' && (
                            <button 
                                onClick={toggleLoop} 
                                className={`btn-icon btn-icon-circle btn-loop ${playerState.isLooping ? 'active' : ''}`}
                                title={playerState.isLooping ? "Looping On" : "Looping Off"}
                            >
                                🔁
                            </button>
                        )}
                    </div>

                    {playerState.isPlaying && (
                        <>
                            <div style={{marginBottom:'4px', fontSize:'11px', color:'var(--text-soft)'}}>Sleep Timer</div>
                            <div className="duration-selector">
                                {[15, 30, 45, 60].map(mins => (
                                    <div key={mins} className={`duration-pill ${timerDuration === mins ? 'active' : ''}`} onClick={() => handleSetTimer(mins)}>{mins}m</div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                </div>
        </section>
    );
};

// Main App Container
const SlumberApp = () => {
    const { user, updateUser, toggleFavourite, addSleepLog, resetApp, isLoading } = useUser();
    const [activeView, setActiveView] = useState('home');
    const [showCheckIn, setShowCheckIn] = useState(false);
    const [showLegal, setShowLegal] = useState(false);
    const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);

    // Player State
    const [playerState, setPlayerState] = useState({ 
        isPlaying: false, 
        trackId: null as string | null, 
        progress: 0, 
        currentTime: 0,
        isLooping: true
    });
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [timerDuration, setTimerDuration] = useState<number | null>(null);

    // Initial load logic
    useEffect(() => {
        if (isLoading) return; 
        checkMorningPrompt();
    }, [isLoading]);

    const checkMorningPrompt = () => {
        const hour = new Date().getHours();
        const today = new Date().toISOString().split('T')[0];
        const hasLog = user.sleepLog.some(l => l.date === today);
        
        // Show if morning (< 12pm) and no log for today
        if (hour < 12 && !hasLog) {
             setShowCheckIn(true);
        }
    };

    const handleCheckInSave = (log: Partial<SleepLog>) => {
        if (log.date && log.quality) {
            addSleepLog(log as SleepLog);
        }
        setShowCheckIn(false);
    };

    // --- PLAYER LOGIC ---
    
    useEffect(() => {
        if (audioRef.current) {
            if (playerState.isPlaying) {
                // Defensive check to avoid playing without source
                if (audioRef.current.src && audioRef.current.src !== window.location.href) {
                     audioRef.current.play().catch(() => {
                        // onError handles fallback, removed unused 'e'
                    });
                }
            } else {
                audioRef.current.pause();
            }
        }
    }, [playerState.isPlaying, playerState.trackId]); // trackId change triggers load, then play

    const playTrack = (trackId: string) => {
        const track = TRACKS.find(t => t.id === trackId);
        if (!track) return;
        
        console.log("Attempting to play:", track.audioUrl);

        const isLooping = track.isLoopable;
        
        // Update state
        setPlayerState(prev => ({ 
            ...prev, 
            trackId, 
            isPlaying: true, 
            progress: 0, 
            currentTime: 0,
            isLooping 
        }));
        
        // Update Audio Element
        if (audioRef.current) {
            audioRef.current.src = track.audioUrl || '';
            audioRef.current.loop = isLooping;
            audioRef.current.currentTime = 0;
            audioRef.current.volume = 1.0;
        }

        setActiveView('player');
        
        const newHist = [trackId, ...user.history.filter(id => id !== trackId)].slice(0, 20);
        updateUser({ history: newHist });
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const current = audioRef.current.currentTime;
            const duration = audioRef.current.duration || 1;
            setPlayerState(prev => ({
                ...prev,
                currentTime: current,
                progress: (current / duration) * 100
            }));
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (audioRef.current) {
            const value = parseFloat(e.target.value);
            const duration = audioRef.current.duration || 1; // Avoid divide by zero
            const time = (value / 100) * duration;
            
            // Update audio
            audioRef.current.currentTime = time;
            
            // Update state immediately for responsiveness
            setPlayerState(prev => ({ 
                ...prev, 
                progress: value, 
                currentTime: time 
            }));
        }
    };
    
    const skipTime = (seconds: number) => {
         if (audioRef.current) {
            let newTime = audioRef.current.currentTime + seconds;
            // Clamp value
            if (newTime < 0) newTime = 0;
            if (newTime > audioRef.current.duration) newTime = audioRef.current.duration;
            
            audioRef.current.currentTime = newTime;
            
            // Update state immediately for responsiveness
            setPlayerState(prev => ({
                ...prev,
                currentTime: newTime,
                progress: (newTime / (audioRef.current?.duration || 1)) * 100
            }));
         }
    };

    const togglePlay = () => {
        setPlayerState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
    };
    
    const toggleLoop = () => {
        const newVal = !playerState.isLooping;
        setPlayerState(prev => ({ ...prev, isLooping: newVal }));
        if (audioRef.current) audioRef.current.loop = newVal;
    };

    const handleSetTimer = (minutes: number) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (timerDuration === minutes) {
            setTimerDuration(null);
            return;
        }
        setTimerDuration(minutes);
        
        timerRef.current = setTimeout(() => {
            const audio = audioRef.current;
            if (audio) {
                const startVolume = audio.volume;
                const stepTime = 100;
                const fadeDuration = 2000;
                const steps = fadeDuration / stepTime;
                const volumeStep = startVolume / steps;

                const fadeInterval = setInterval(() => {
                    if (audio.volume > volumeStep) {
                        audio.volume -= volumeStep;
                    } else {
                        audio.volume = 0;
                        audio.pause();
                        setPlayerState(prev => ({ ...prev, isPlaying: false }));
                        setTimerDuration(null);
                        clearInterval(fadeInterval);
                        // Reset volume for next time
                        audio.volume = startVolume;
                    }
                }, stepTime);
            } else {
                setPlayerState(prev => ({ ...prev, isPlaying: false }));
                setTimerDuration(null);
            }
        }, minutes * 60 * 1000);
    };

    const handleTrackClick = (track: Track) => {
        setSelectedTrack(track);
        setActiveView('detail');
    };

    // Calculate recommended tracks
    let recommended = TRACKS.filter(t => t.isNew).slice(0, 6);
    if (user.sounds.length > 0) {
            const types: string[] = [];
            if (user.sounds.includes('stories')) types.push('stories');
            if (user.sounds.includes('music')) types.push('music');
            if (user.sounds.includes('white-noise')) types.push('noise');
            if (user.sounds.includes('rain') || user.sounds.includes('ocean')) types.push('soundscapes');
            if (types.length > 0) {
            recommended = TRACKS.filter(t => types.includes(t.type)).slice(0, 6);
            }
    }

    const NavBar = () => {
        if (activeView === 'player') return null;
        return (
            <nav id="bottom-nav">
                <button className={`nav-item ${activeView === 'home' ? 'active' : ''}`} onClick={() => setActiveView('home')}>
                    <span className="icon">🏠</span><span>Home</span>
                </button>
                <button className={`nav-item ${activeView === 'library' || activeView === 'detail' ? 'active' : ''}`} onClick={() => setActiveView('library')}>
                    <span className="icon">🎧</span><span>Library</span>
                </button>
                <button className={`nav-item ${activeView === 'profile' || activeView === 'sleep-log' ? 'active' : ''}`} onClick={() => setActiveView('profile')}>
                    <span className="icon">👤</span><span>Profile</span>
                </button>
            </nav>
        );
    };

    if (isLoading) {
        return (
            <div id="app" className="flex items-center justify-center bg-[#050716] text-white h-screen">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-400 text-sm">Waking up...</p>
                </div>
            </div>
        );
    }

    return (
        <div id="app">
            {showCheckIn && <CheckInModal onClose={() => setShowCheckIn(false)} onSave={handleCheckInSave} />}
            {showLegal && <LegalModal onClose={() => setShowLegal(false)} />}
            
            <audio 
                ref={audioRef} 
                onTimeUpdate={handleTimeUpdate} 
                onEnded={() => {
                    if (!playerState.isLooping) setPlayerState(p => ({...p, isPlaying: false}));
                }}
                onError={() => {
                    console.error("Audio playback error"); 
                    if (audioRef.current && audioRef.current.src !== PLACEHOLDER_AUDIO) {
                         console.log("Falling back to placeholder");
                         audioRef.current.src = PLACEHOLDER_AUDIO;
                         if(playerState.isPlaying) audioRef.current.play();
                    } else {
                         setPlayerState(p => ({...p, isPlaying: false}));
                    }
                }}
            />
            
            <main>
                <HomeView 
                    user={user} 
                    activeView={activeView}
                    setShowCheckIn={setShowCheckIn}
                    playTrack={playTrack}
                    handleTrackClick={handleTrackClick}
                    recommended={recommended}
                />
                <LibraryView 
                    user={user} 
                    activeView={activeView}
                    updateUser={updateUser}
                    playTrack={playTrack}
                    handleTrackClick={handleTrackClick}
                />
                <ProfileView 
                    user={user}
                    activeView={activeView}
                    setActiveView={setActiveView}
                    updateUser={updateUser}
                    setShowCheckIn={setShowCheckIn}
                    setShowLegal={setShowLegal}
                    resetApp={resetApp}
                />
                <section className={`view ${activeView === 'sleep-log' ? 'active' : ''}`}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                        <button onClick={() => setActiveView('profile')} className="btn btn-ghost">← Back</button>
                        <div className="pill">Sleep Log</div>
                    </div>
                    <SleepTracker />
                </section>
                <DetailView 
                    selectedTrack={selectedTrack}
                    activeView={activeView}
                    setActiveView={setActiveView}
                    playTrack={playTrack}
                    toggleFavourite={toggleFavourite}
                    user={user}
                />
                <PlayerView 
                    activeView={activeView}
                    setActiveView={setActiveView}
                    playerState={playerState}
                    user={user}
                    handleSeek={handleSeek}
                    skipTime={skipTime}
                    togglePlay={togglePlay}
                    toggleLoop={toggleLoop}
                    timerDuration={timerDuration}
                    handleSetTimer={handleSetTimer}
                    toggleFavourite={toggleFavourite}
                />
            </main>
            <NavBar />
        </div>
    );
};

export default function App() {
  return (
      <UserProvider>
          <SlumberApp />
      </UserProvider>
  );
}